import React, { useEffect, useState } from "react";
import {
    Database,
    Loader2,
    RefreshCw,
    Sparkles,
    X,
    XCircle,
    PlayCircle,
    Trash2,
    ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUS_LABELS = {
    fetched: { label: "Çekildi", color: "bg-slate-100 text-slate-600" },
    processing: { label: "İşleniyor", color: "bg-amber-100 text-amber-700" },
    draft_created: { label: "Taslak Oluştu", color: "bg-data-green/10 text-data-green" },
    rejected: { label: "Reddedildi", color: "bg-data-red/10 text-data-red" },
    error: { label: "Hata", color: "bg-data-red/15 text-data-red" },
};

export default function RawNews() {
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({ page: 1, total: 0, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: "fetched", min_score: "" });
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(null);
    const [batchBusy, setBatchBusy] = useState(false);

    const load = async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(page), page_size: "20" });
            if (filter.status) params.set("status", filter.status);
            if (filter.min_score) params.set("min_score", filter.min_score);
            const { data } = await api.get(`/admin/news-automation/raw?${params}`);
            setItems(data.items || []);
            setMeta({ page: data.page, total: data.total, pages: data.pages });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(1); }, [filter]);

    const reject = async (id) => {
        try {
            await api.put(`/admin/news-automation/raw/${id}/reject`);
            toast.success("Reddedildi.");
            await load(meta.page);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const processOne = async (id) => {
        setBusy(id);
        try {
            const { data } = await api.post(`/admin/news-automation/raw/${id}/process`);
            if (data.status === "draft_created") toast.success(`Taslak oluşturuldu (skor: ${data.score})`);
            else if (data.status === "rejected") toast.warning(`Reddedildi: ${data.reason}`);
            else toast.error(`Hata: ${data.reason || "bilinmiyor"}`);
            await load(meta.page);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const processBatch = async () => {
        setBatchBusy(true);
        try {
            const { data } = await api.post("/admin/news-automation/raw/process-pending?max_batch=10");
            toast.success(`İşlendi: ${data.processed} → ${data.drafts} taslak / ${data.rejected} ret / ${data.errors} hata`);
            await load(meta.page);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBatchBusy(false);
        }
    };

    const removeRaw = async (id) => {
        if (!window.confirm("Ham haber silinsin mi?")) return;
        try {
            await api.delete(`/admin/news-automation/raw/${id}`);
            toast.success("Silindi.");
            await load(meta.page);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const scoreColor = (s) => {
        if (s == null) return "bg-slate-100 text-slate-500";
        if (s >= 70) return "bg-data-green/15 text-data-green";
        if (s >= 40) return "bg-amber-100 text-amber-700";
        return "bg-data-red/10 text-data-red";
    };

    return (
        <div data-testid="admin-raw-news-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <Database className="w-6 h-6 text-teal" /> Ham Haberler
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">
                        RSS'den çekilen ham haberler — relevance puanı, AI işleme, taslak oluşturma kuyruğu.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={filter.status}
                        onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                        className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
                        data-testid="raw-news-filter-status"
                    >
                        <option value="">Tüm Durumlar</option>
                        <option value="fetched">Bekleyen</option>
                        <option value="draft_created">Taslak Oluştu</option>
                        <option value="rejected">Reddedildi</option>
                        <option value="error">Hata</option>
                    </select>
                    <input
                        type="number"
                        placeholder="Min Skor"
                        value={filter.min_score}
                        onChange={(e) => setFilter({ ...filter, min_score: e.target.value })}
                        min={0}
                        max={100}
                        className="w-24 px-3 py-2 rounded-md border border-slate-300 text-sm"
                    />
                    <button
                        onClick={processBatch}
                        disabled={batchBusy}
                        className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="raw-news-process-batch"
                    >
                        {batchBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        AI Toplu İşle (10)
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Bu filtreye uygun ham haber yok.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="raw-news-table">
                        <thead>
                            <tr className="bg-light-bg text-left text-xs uppercase tracking-wider text-deep-blue/70">
                                <th className="px-4 py-3">Başlık</th>
                                <th className="px-4 py-3">Kaynak</th>
                                <th className="px-4 py-3">Skor</th>
                                <th className="px-4 py-3">Durum</th>
                                <th className="px-4 py-3">Tarih</th>
                                <th className="px-4 py-3 w-44">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => {
                                const st = STATUS_LABELS[r.status] || { label: r.status, color: "bg-slate-100 text-slate-600" };
                                return (
                                    <tr key={r.id} className="border-t border-slate-100 hover:bg-light-bg/30">
                                        <td className="px-4 py-3 max-w-md">
                                            <div className="font-semibold text-dark-navy text-sm line-clamp-2">{r.original_title}</div>
                                            <a href={r.original_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-teal-700 hover:underline">
                                                Orijinali aç <ExternalLink className="w-2.5 h-2.5" />
                                            </a>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-deep-blue/80">{r.source_name}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-1 rounded text-[11px] font-bold font-data ${scoreColor(r.relevance_score)}`}>
                                                {r.relevance_score == null ? "—" : r.relevance_score}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${st.color}`}>{st.label}</span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-deep-blue/70 font-data">
                                            {r.created_at ? new Date(r.created_at).toLocaleDateString("tr-TR") : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => setActive(r)} title="Detay" className="p-1.5 rounded-md hover:bg-light-bg text-xs font-semibold text-deep-blue">
                                                    Detay
                                                </button>
                                                {r.status === "fetched" && (
                                                    <button
                                                        onClick={() => processOne(r.id)}
                                                        disabled={busy === r.id}
                                                        title="AI ile İşle"
                                                        className="p-1.5 rounded-md hover:bg-light-bg disabled:opacity-50"
                                                        data-testid={`raw-news-process-${r.id}`}
                                                    >
                                                        {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin text-teal" /> : <PlayCircle className="w-4 h-4 text-teal-700" />}
                                                    </button>
                                                )}
                                                {r.status === "fetched" && (
                                                    <button onClick={() => reject(r.id)} title="Reddet" className="p-1.5 rounded-md hover:bg-light-bg">
                                                        <XCircle className="w-4 h-4 text-data-red" />
                                                    </button>
                                                )}
                                                <button onClick={() => removeRaw(r.id)} title="Sil" className="p-1.5 rounded-md hover:bg-light-bg">
                                                    <Trash2 className="w-4 h-4 text-data-red/70" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {meta.pages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                    <button onClick={() => load(meta.page - 1)} disabled={meta.page <= 1} className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-sm disabled:opacity-50">‹</button>
                    <span className="text-sm font-semibold text-deep-blue">{meta.page} / {meta.pages} — toplam {meta.total}</span>
                    <button onClick={() => load(meta.page + 1)} disabled={meta.page >= meta.pages} className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-sm disabled:opacity-50">›</button>
                </div>
            )}

            {active && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="raw-news-detail">
                    <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-2xl w-full my-8">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-dark-navy line-clamp-1">{active.original_title}</h2>
                            <button onClick={() => setActive(null)} className="p-1.5 rounded-md hover:bg-light-bg shrink-0">
                                <X className="w-4 h-4 text-deep-blue" />
                            </button>
                        </div>
                        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                            <div className="flex items-center gap-3 text-xs text-deep-blue/70">
                                <span className="font-semibold">{active.source_name}</span>
                                {active.author && <span>· {active.author}</span>}
                                {active.original_date && <span>· {new Date(active.original_date).toLocaleDateString("tr-TR")}</span>}
                            </div>
                            {active.image_url && <img src={active.image_url} alt="" className="w-full max-h-64 object-cover rounded-md" />}
                            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: active.original_content }} />
                            <a href={active.original_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-teal-700 hover:underline">
                                Orijinal kaynak <ExternalLink className="w-3 h-3" />
                            </a>
                            {active.relevance_score != null && (
                                <div className="bg-light-bg rounded-md p-3">
                                    <div className="text-xs uppercase tracking-wider font-bold text-deep-blue/80">Relevance Skoru</div>
                                    <div className="font-data text-2xl font-bold text-dark-navy">{active.relevance_score}/100</div>
                                </div>
                            )}
                            {active.rejection_reason && (
                                <div className="bg-data-red/5 border-l-4 border-data-red rounded-md p-3">
                                    <div className="text-xs uppercase tracking-wider font-bold text-data-red">Reddetme Nedeni</div>
                                    <div className="text-sm text-deep-blue/85 mt-1">{active.rejection_reason}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
